/**
 * The shell both bid pages are: a window, a search, a table, a footer.
 *
 * Dispatchers Bids and Drivers Bids differ in their COLUMNS and nothing else — same window set, same
 * debounce, same paging, same empty and error states. Written out twice, the four defects whose fixes are
 * commented below would have to be remembered twice, and the copies would drift the first time one of
 * them was touched. Each page supplies its title, its header cells and how to draw a row.
 */
import * as React from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import CircularProgress from '@mui/joy/CircularProgress'
import Input from '@mui/joy/Input'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import { ANALYTICS_DEFAULT_WINDOW_DAYS, ANALYTICS_WINDOW_DAYS, ANALYTICS_WINDOW_LABEL } from '@numeo/types-main-mobile'
import type { BidListQuery } from '@/api/bidList'

/** The server's own set, not a second copy of it — the two drifted the first time they were written. */
const WINDOWS = ANALYTICS_WINDOW_DAYS.map((value) => ({ value, label: ANALYTICS_WINDOW_LABEL[value] }))

const LIMIT = 50
/** Long enough that a staff member stops typing first; short enough not to fire on every keystroke. */
const SEARCH_DEBOUNCE_MS = 400
/** What the window falls back to when a search makes "All time" unavailable. */
const SEARCHABLE_DEFAULT_DAYS = ANALYTICS_DEFAULT_WINDOW_DAYS

/** What every bid page's response carries, whatever its rows are. */
interface BidPage<TRow extends { id: string }> {
    items: TRow[]
    total: number
    totalIsCapped: boolean
    page: number
    limit: number
}

interface BidListPageProps<TRow extends { id: string }> {
    icon: React.ReactNode
    title: string
    subtitle: string
    searchPlaceholder: string
    /** The column headers, as `<th>` elements — the widths are the page's business, not the shell's. */
    head: React.ReactNode
    renderRow: (row: TRow) => React.ReactNode
    fetchPage: (query: BidListQuery, signal?: AbortSignal) => Promise<BidPage<TRow>>
    /** Shown when the window holds nothing, e.g. "No bids in this window." */
    emptyText: string
}

export function BidListPage<TRow extends { id: string }>({
    icon,
    title,
    subtitle,
    searchPlaceholder,
    head,
    renderRow,
    fetchPage,
    emptyText,
}: BidListPageProps<TRow>) {
    const [days, setDays] = React.useState<number>(ANALYTICS_DEFAULT_WINDOW_DAYS)
    const [page, setPage] = React.useState(1)
    const [searchInput, setSearchInput] = React.useState('')
    const [search, setSearch] = React.useState('')
    const [data, setData] = React.useState<BidPage<TRow> | null>(null)
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState('')

    /**
     * Changing WHAT is asked for returns to the first page, in the same handler that changes it.
     *
     * As its own effect this fired a render LATE: moving from 30 to 90 days while on page 4 fetched
     * `{days: 90, page: 4}` first and `{days: 90, page: 1}` after, and the browser aborting the first does
     * not stop the server — count included — from running it.
     */
    const changeWindow = React.useCallback((next: number) => {
        setDays(next)
        setPage(1)
    }, [])

    /**
     * Debounced, so typing a name is one request rather than eleven, and the window moves in the same
     * commit — the server refuses a search over all time.
     *
     * Keyed on `searchInput` ALONE. With `days` in the deps, every window change re-armed this timer and
     * fired its `setPage(1)` 400 ms later: a `Next` click inside that window was silently reverted and the
     * page fetched twice. The all-time swap reads the current window through the functional updater rather
     * than closing over it, which is what lets the dep go.
     */
    React.useEffect(() => {
        const term = searchInput.trim()
        const timer = setTimeout(() => {
            setSearch(term)
            setPage(1)
            if (term) setDays((current) => (current === 0 ? SEARCHABLE_DEFAULT_DAYS : current))
        }, SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [searchInput])

    /**
     * The fetcher, held rather than depended on.
     *
     * In the deps it is a trap handed to every future caller: both pages pass a module-level const today,
     * so the identity is stable, but `fetchPage={(q, s) => fetchX(q, s)}` is the natural thing to write
     * when a page wants to add a parameter — and that is a new identity every render, so the effect
     * re-runs forever against a count query that costs 27 ms at its worst. Invisible at the call site,
     * which is why it is closed here rather than written down.
     */
    const fetchRef = React.useRef(fetchPage)
    fetchRef.current = fetchPage

    React.useEffect(() => {
        const controller = new AbortController()
        setLoading(true)
        setError('')
        // Cleared with the query. Keeping the old rows while a new one fails left the selector saying
        // "All time" above the 30-day rows, with the error alert on top — read as the all-time answer.
        setData(null)
        fetchRef
            .current({ days, page, limit: LIMIT, search: search || undefined }, controller.signal)
            .then((result) => {
                if (!controller.signal.aborted) setData(result)
            })
            .catch((err: Error) => {
                if (!controller.signal.aborted) setError(err.message)
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false)
            })
        return () => controller.abort()
        // `days`, `page` and `search` only — what this effect is actually about.
    }, [days, page, search])

    // When the count is capped, `total` IS the cap, so this is also the deepest page the server will
    // serve: it refuses an offset at or past that same number. Letting `Next` stay live past it — which an
    // earlier `|| totalIsCapped` did — put a 400 and an error alert in front of anyone who reached the
    // last page and pressed on.
    const lastPage = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1
    const canGoNext = !!data && data.page < lastPage

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                {icon}
                <Typography level="h2">{title}</Typography>
            </Box>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 2 }}>
                {subtitle}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <Select value={days} onChange={(_, value) => changeWindow(value ?? ANALYTICS_DEFAULT_WINDOW_DAYS)} sx={{ minWidth: 170 }}>
                    {WINDOWS.map((w) => (
                        <Option key={w.value} value={w.value} disabled={w.value === 0 && !!searchInput.trim()}>
                            {w.label}
                            {w.value === 0 && !!searchInput.trim() ? ' — not with a search' : ''}
                        </Option>
                    ))}
                </Select>
                <Input
                    placeholder={searchPlaceholder}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    sx={{ minWidth: 280, flex: 1, maxWidth: 420 }}
                />
            </Box>

            {error && (
                <Alert color="danger" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && data && data.items.length === 0 && (
                <Sheet variant="soft" sx={{ p: 4, borderRadius: 'md', textAlign: 'center' }}>
                    <Typography level="body-sm" textColor="text.tertiary">
                        {emptyText}
                    </Typography>
                </Sheet>
            )}

            {!loading && data && data.items.length > 0 && (
                <>
                    <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                        <Table stickyHeader hoverRow sx={{ '--Table-headerUnderlineThickness': '1px' }}>
                            <thead>
                                <tr>{head}</tr>
                            </thead>
                            <tbody>{data.items.map((row) => renderRow(row))}</tbody>
                        </Table>
                    </Sheet>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                        <Typography level="body-xs" textColor="text.tertiary">
                            {/* `10,000+`, because the count stops there on purpose: an exact total over an
                                append-only table is a full-table count on every click, for a number nobody
                                acts on past the first few pages. */}
                            {data.total.toLocaleString('en-US')}
                            {data.totalIsCapped ? '+' : ''} bids · page {data.page} of {lastPage}
                            {data.totalIsCapped ? '+' : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="sm" variant="outlined" disabled={data.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                                Previous
                            </Button>
                            <Button size="sm" variant="outlined" disabled={!canGoNext} onClick={() => setPage((p) => p + 1)}>
                                Next
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    )
}
